import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/common/SEO';

const implementationPhases = [
  { phase: 'Assess', num: '01', desc: 'Map current workflows, identify AI readiness, assess integration complexity, and define success metrics before any deployment.' },
  { phase: 'Deploy', num: '02', desc: 'Configure agents, connect to enterprise systems, set up data sources, and run parallel testing against manual baselines.' },
  { phase: 'Govern', num: '03', desc: 'Configure approval gates, audit logging, role-based access controls, policy-as-code guardrails, and escalation paths.' },
  { phase: 'Scale', num: '04', desc: 'Expand automation to additional teams and functions, refine agents with feedback data, and build the internal AI adoption roadmap.' },
];

const agentCategories = [
  { category: 'Cloud & Infrastructure', agents: ['Cloud Architect Agent', 'Governance Audit Agent'], link: '/agents/cloud-architect' },
  { category: 'Finance & Accounting', agents: ['CPA Agent', 'KYC Onboarding Agent'], link: '/agents/cpa' },
  { category: 'Data Engineering', agents: ['Data Engineer Agent', 'ETL Agent', 'SQL Agent', 'DBA Agent'], link: '/agents/data-engineering' },
  { category: 'HR & Recruitment', agents: ['Interview Coach Agent', 'Recruiter Agent'], link: '/agents/recruiter-agent' },
  { category: 'Sales & GTM', agents: ['SDR Agent', 'Email Assistant Agent'], link: '/agents/sdr-agent' },
  { category: 'AI Quality & Governance', agents: ['LLM Judge Agent', 'Schema Governance Designer'], link: '/agents/llm-as-judge' },
];

const deploymentOptions = [
  { option: 'Cloud', desc: 'Deploy via the Eagentix cloud platform. Fastest time to value with managed infrastructure, automatic updates, and enterprise SLAs.' },
  { option: 'Private Cloud', desc: 'Deploy on your own cloud infrastructure (AWS, Azure, GCP). Data stays in your environment. Full control over networking and security.' },
  { option: 'On-Premises', desc: 'Deploy on your own on-premises hardware. Ideal for enterprises with strict data residency requirements and existing data centre capacity.' },
  { option: 'Air-Gapped', desc: 'Fully isolated deployment with no external network connectivity. Available for highly regulated industries and government environments.' },
];

const faqs = [
  {
    q: 'What does AI agent implementation involve?',
    a: 'AI agent implementation includes model selection, domain knowledge integration, workflow design, governance setup, tool and API connections, testing, and production deployment with monitoring.',
  },
  {
    q: 'How does Eagentix approach AI agent implementation?',
    a: 'Eagentix uses a four-phase approach: Assess (understand current workflows and AI readiness), Deploy (configure and connect agents to enterprise systems), Govern (set up approval gates, audit trails, and policies), Scale (expand automation across teams and functions).',
  },
  {
    q: 'What deployment options does Eagentix support?',
    a: 'Eagentix supports cloud deployment via the Eagentix platform, private cloud deployment on customer infrastructure, on-premises deployment, and air-gapped deployment for regulated environments.',
  },
  {
    q: 'Which enterprise systems can Eagentix agents integrate with?',
    a: 'Eagentix agents can integrate with cloud platforms (AWS, Azure, GCP), ERP and accounting systems, HRIS platforms, CRM tools, data warehouses (Snowflake, BigQuery, Databricks), and custom APIs.',
  },
  {
    q: 'Does Eagentix provide AI implementation consulting?',
    a: 'Yes. Eagentix offers AI strategy consulting alongside agent deployment, helping enterprises identify the highest-value automation opportunities and build a roadmap for AI adoption.',
  },
];

export default function AIAgentImplementation() {
  const sectionLight = { padding: '80px 0', background: '#f8fafc' };
  const sectionWhite = { padding: '80px 0', background: 'white' };
  const sectionDark = { padding: '80px 0', background: '#0f172a' };
  const container = { maxWidth: '1100px', margin: '0 auto', padding: '0 24px' };
  const card = { borderRadius: '20px', border: '1px solid rgba(148,163,184,0.16)', background: 'white', padding: '28px 32px' };

  return (
    <>
      <SEO
        title="AI Agent Implementation for Enterprise | Eagentix"
        description="Eagentix handles enterprise AI agent implementation end-to-end — from strategy and model selection to production deployment, governance setup, and ongoing optimisation."
        pathname="/use-cases/ai-agent-implementation"
      />

      {/* Hero */}
      <section style={{ ...sectionDark, paddingTop: '120px' }}>
        <div style={container}>
          <div style={{ maxWidth: '800px' }}>
            <span style={{ color: '#60a5fa', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.15em' }}>
              Use Case · Implementation
            </span>
            <h1 style={{ fontSize: '3rem', fontWeight: 800, color: 'white', letterSpacing: '-0.03em', margin: '16px 0 24px' }}>
              AI Agent Implementation for Enterprise
            </h1>
            <p style={{ fontSize: '1.2rem', color: '#cbd5e1', lineHeight: 1.75, marginBottom: '36px' }}>
              Eagentix handles enterprise AI agent implementation end-to-end — from strategy and model selection to production deployment, governance setup, and ongoing optimisation.
            </p>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <a
                href="https://app.eagentix.com/register"
                target="_blank"
                rel="noopener noreferrer"
                style={{ background: '#0071e3', color: 'white', padding: '14px 28px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem' }}
              >
                Get Started
              </a>
              <a
                href="/#contact"
                style={{ background: 'rgba(255,255,255,0.1)', color: 'white', padding: '14px 28px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem', border: '1px solid rgba(255,255,255,0.2)' }}
              >
                Talk to Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Implementation phases */}
      <section style={sectionLight}>
        <div style={container}>
          <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '12px' }}>
            Implementation Phases
          </h2>
          <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: 1.75, marginBottom: '40px', maxWidth: '680px' }}>
            Eagentix follows a structured four-phase approach to enterprise AI agent implementation.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px' }}>
            {implementationPhases.map((phase, i) => (
              <div key={i} style={card}>
                <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: '8px' }}>{phase.num}</div>
                <h3 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#0071e3', marginBottom: '10px' }}>{phase.phase}</h3>
                <p style={{ color: '#475569', fontSize: '0.95rem', lineHeight: 1.65, margin: 0 }}>{phase.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What Eagentix implements */}
      <section style={sectionWhite}>
        <div style={container}>
          <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '12px' }}>
            What Eagentix Implements
          </h2>
          <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: 1.75, marginBottom: '40px', maxWidth: '680px' }}>
            Agent categories covering the highest-value enterprise automation use cases.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '20px' }}>
            {agentCategories.map((cat, i) => (
              <Link
                key={i}
                to={cat.link}
                style={{ ...card, textDecoration: 'none', display: 'block' }}
              >
                <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#0071e3', marginBottom: '10px' }}>{cat.category}</h3>
                <ul style={{ margin: 0, padding: '0 0 0 18px', color: '#475569', fontSize: '0.92rem', lineHeight: 1.8 }}>
                  {cat.agents.map((a, j) => <li key={j}>{a}</li>)}
                </ul>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Deployment options */}
      <section style={sectionLight}>
        <div style={container}>
          <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '12px' }}>
            Deployment Options
          </h2>
          <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: 1.75, marginBottom: '40px', maxWidth: '680px' }}>
            Eagentix supports four deployment models to meet enterprise data residency, security, and compliance requirements.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' }}>
            {deploymentOptions.map((opt, i) => (
              <div key={i} style={card}>
                <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a', marginBottom: '10px' }}>{opt.option}</h3>
                <p style={{ color: '#475569', fontSize: '0.95rem', lineHeight: 1.65, margin: 0 }}>{opt.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section style={sectionWhite}>
        <div style={container}>
          <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '40px' }}>
            Frequently Asked Questions
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {faqs.map((faq, i) => (
              <div key={i} style={card}>
                <h3 style={{ color: '#0071e3', fontSize: '1.05rem', fontWeight: 700, margin: '0 0 12px 0' }}>{faq.q}</h3>
                <p style={{ color: '#475569', fontSize: '1rem', lineHeight: 1.75, margin: 0 }}>{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={sectionDark}>
        <div style={{ ...container, textAlign: 'center' }}>
          <h2 style={{ fontSize: '2.2rem', fontWeight: 800, color: 'white', letterSpacing: '-0.03em', marginBottom: '16px' }}>
            Start Your AI Implementation
          </h2>
          <p style={{ color: '#cbd5e1', fontSize: '1.1rem', lineHeight: 1.75, marginBottom: '36px', maxWidth: '600px', margin: '0 auto 36px' }}>
            Eagentix handles strategy, deployment, governance, and scale — so your team gets to outcomes faster.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a
              href="https://app.eagentix.com/register"
              target="_blank"
              rel="noopener noreferrer"
              style={{ background: '#0071e3', color: 'white', padding: '14px 28px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem' }}
            >
              Start Free
            </a>
            <a
              href="/#contact"
              style={{ background: 'rgba(255,255,255,0.1)', color: 'white', padding: '14px 28px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem', border: '1px solid rgba(255,255,255,0.2)' }}
            >
              Book a Meeting
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
